import * as React from 'react'
import { Option } from 'uiComponents/combobox/combobox'
import { Reveal } from 'uiComponents/reveal/reveal'
import { PRODUCTS } from 'capacity/copy'
import { OptionItemsCoverage } from 'capacity/api/capacityGateway'
import { TableSort } from 'capacity/derive/tableSort'
import { CapacityProductsStatus } from 'capacity/hooks/useCapacityProducts'
import { FilteredEmpty, ProductsToolbar } from './productsToolbar'
import { ProductsColumn, ProductsTable } from './productsTable'
import { ProductRow } from './productRow'
import { ProductRowModel, ProductTypeKey, SaleFilter, SORT_KEYS, TypeFilter } from './rowModel'

interface ProductsSectionProps {
    status: CapacityProductsStatus
    onRetry: () => void
    optionItems: OptionItemsCoverage
    allRows: ProductRowModel[]
    visibleRows: ProductRowModel[]
    sort: TableSort | null
    onSort: (key: string) => void
    addOptions: Option[]
    onAdd: (productId: string) => void
    types: ProductTypeKey[]
    typeFilter: TypeFilter
    onTypeFilter: (value: TypeFilter) => void
    saleFilter: SaleFilter
    onSaleFilter: (value: SaleFilter) => void
    onToggle: (productId: string) => void
    onAmountChange: (row: ProductRowModel, next: number) => void
    numericIdOf: (productId: string) => number | undefined
    numericIdsLoading: boolean
    takesUpTooltip: string
    consequence: string | null
}

export function ProductsSection(props: ProductsSectionProps) {
    const {
        status,
        onRetry,
        optionItems,
        allRows,
        visibleRows,
        sort,
        onSort,
        addOptions,
        onAdd,
        types,
        typeFilter,
        onTypeFilter,
        saleFilter,
        onSaleFilter,
        onToggle,
        onAmountChange,
        numericIdOf,
        numericIdsLoading,
        takesUpTooltip,
        consequence,
    } = props

    const lastConsequence = React.useRef<string | null>(null)
    if (consequence) {
        lastConsequence.current = consequence
    }

    const columns: ProductsColumn[] = [
        { key: SORT_KEYS.product, header: PRODUCTS.columns.product, sortable: true },
        { key: SORT_KEYS.onSale, header: PRODUCTS.columns.onSale, width: 'minmax(5.5rem, 7rem)', sortable: true },
        {
            key: SORT_KEYS.takesUp,
            header: PRODUCTS.columns.takesUp,
            width: 'minmax(11rem, 14rem)',
            headerTooltip: takesUpTooltip,
            sortable: true,
        },
        {
            key: SORT_KEYS.uses,
            header: PRODUCTS.columns.uses,
            align: 'right',
            width: 'minmax(6.5rem, 9rem)',
            headerTooltip: PRODUCTS.usesTooltip,
            sortable: true,
        },
    ]

    return (
        <section className="capacity-products" aria-labelledby="capacity-products-heading">
            <h2 id="capacity-products-heading" className="capacity-card-title">
                {PRODUCTS.heading}
            </h2>
            <ProductsToolbar
                addOptions={addOptions}
                onAdd={onAdd}
                types={types}
                typeFilter={typeFilter}
                onTypeFilter={onTypeFilter}
                saleFilter={saleFilter}
                onSaleFilter={onSaleFilter}
                shown={visibleRows.length}
                total={allRows.length}
                loading={status === 'loading'}
            />
            <ProductsTable
                columns={columns}
                sort={sort}
                onSort={onSort}
                loading={status === 'loading'}
                error={status === 'error'}
                onRetry={onRetry}
                empty={allRows.length === 0}
                filteredEmpty={
                    <FilteredEmpty
                        typeFilter={typeFilter}
                        saleFilter={saleFilter}
                        onShowAll={() => {
                            onTypeFilter('all')
                            onSaleFilter('all')
                        }}
                    />
                }
                rows={visibleRows.map((row) => (
                    <ProductRow
                        key={row.key}
                        row={row}
                        onToggle={() => onToggle(row.member.id)}
                        onAmountChange={(next) => onAmountChange(row, next)}
                        numericId={numericIdOf(row.member.id)}
                        numericIdLoading={numericIdsLoading}
                    />
                ))}
            >
                <Reveal open={optionItems !== 'full'}>
                    <p className="capacity-products-note">
                        {optionItems === 'failed' ? PRODUCTS.optionItemsFailed : PRODUCTS.optionItemsCapped}
                    </p>
                </Reveal>
                <Reveal open={!!consequence}>
                    <p className="capacity-products-note">{consequence ?? lastConsequence.current}</p>
                </Reveal>
            </ProductsTable>
        </section>
    )
}
